import Table from '@/components/common/table/Table';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useMinMaxContext } from '@/context/minMaxContext/MinMaxContextProvider';
import { useModal } from '@/components/common/modal/ModalContext';
import CreateMinMaxSetting from './CreateMinMaxSetting';

import ActionButtons from '@/components/common/ActionButtons';
import { FaTrash } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';

/**
 * MinMaxTable component displays a paginated, sortable table of min-max settings for inventory items.
 *
 * Features:
 * - Fetches and displays min-max records from context.
 * - Supports pagination, sorting, and searching.
 * - Allows editing and deleting of min-max settings via action buttons.
 * - Integrates with modal dialogs for editing.
 *
 * Context:
 * - Uses `useMinMaxContext` for data and actions.
 * - Uses `useModal` for modal management.
 *
 * @component
 * @returns {JSX.Element} The rendered MinMaxTable component.
 */
const MinMaxTable = () => {
  const {
    minMaxSettingsData, //acutal data->array of min-max records
    totalElements,
    currentPage,
    pageSize,
    sortBy,
    sortDir,
    setCurrentPage,
    setSortBy,
    setSortDir,
    getAllMinMaxSettingsAPI,
    searchQuery,
    deleteMinMaxSettingsAPI,
    clearFormData,
  } = useMinMaxContext(); //customized hook to access min-max context
  const { openModal, closeModal } = useModal(); //show or hide modal globally

  // const handleOpenModal = useCallback( //useCallback to memoize the function(prevents unnecessary re-creation of this function on every render)
  const handleOpenModal = useCallback(
    (user) => {
      clearFormData();
      openModal(
        <CreateMinMaxSetting closeModal={closeModal} data={user} />,
        'Update MinMax Setting',
      );
    },
    [clearFormData, openModal, closeModal],
  );

  const handleSortChange = ({ field, direction }) => {
    setSortBy(field); // Update the sortBy field
    setSortDir(direction); // Update the sort direction
    getAllMinMaxSettingsAPI(
      currentPage,
      pageSize,
      searchQuery,
      field,
      direction,
    ); // Fetch sorted data
  };

  const renderActionButtons = useMemo(
    () => (user) => (
      <ActionButtons
        actions={[
          {
            icon: <MdEdit />,
            onClick: () => handleOpenModal(user),
            tooltip: 'Edit MinMax',
            className: 'text-gray-500 pr-6',
          },
          {
            icon: <FaTrash />,
            onClick: () => deleteMinMaxSettingsAPI(user.id),
            tooltip: 'Delete MinMax',
            className: 'text-red-500 pr-6',
          },
        ]}
      />
    ),
    [handleOpenModal, deleteMinMaxSettingsAPI],
  );

  useEffect(() => {
    getAllMinMaxSettingsAPI();
  }, [currentPage]);

  const columns = useMemo(
    () => [
      {
        header: 'Product Group Name ',
        field: 'mainProductGroup.powerBiMainProductGroupName',
      },
      { header: 'Product Type', field: 'productType' },
      { header: 'Min', field: 'min' },
      { header: 'Max', field: 'max' },
      { header: 'Action', render: renderActionButtons },
    ],
    [deleteMinMaxSettingsAPI, renderActionButtons],
  );
  return (
    <Table
      columns={columns}
      data={minMaxSettingsData}
      totalElements={totalElements}
      currentPage={currentPage}
      pageSize={pageSize}
      onPageChange={(newPage) => setCurrentPage(newPage)}
      keyField="id"
      onSortChange={handleSortChange} // Pass sorting handler
      sortConfig={{ field: sortBy, direction: sortDir }} // Pass current sorting state
    />
  );
};

export default MinMaxTable;
